import { useState, useEffect, lazy, Suspense } from 'react';
import { Volume2, Settings, ChevronDown, Sparkles, ArrowUp, Home, Search, Moon, Sun, WifiOff, Minus, Plus, Contrast } from 'lucide-react';
import { trackEvent } from '../firebase';
import {
  trackVisit,
  trackLanguageUsage,
  trackSectionView,
  trackTextToSpeech,
  trackSessionStart,
  trackSessionEnd
} from '../services/statsService';
import AdminDashboard from './AdminDashboard';
import SearchBar from './SearchBar';
import SkeletonLoader from './SkeletonLoader';

// Lazy load section components for better initial load performance
const HealthSection = lazy(() => import('./HealthSection'));
const WarningSignsSection = lazy(() => import('./WarningSignsSection'));
const MentalHealthSection = lazy(() => import('./MentalHealthSection'));
const AppointmentsSection = lazy(() => import('./AppointmentsSection'));
const ResourcesSection = lazy(() => import('./ResourcesSection'));
const ConditionsSection = lazy(() => import('./ConditionsSection'));
const LaborDeliverySection = lazy(() => import('./LaborDeliverySection'));
const MedicationsSection = lazy(() => import('./MedicationsSection'));
const BirthControlSection = lazy(() => import('./BirthControlSection'));
const BreastfeedingSection = lazy(() => import('./BreastfeedingSection'));
const BabyCareSection = lazy(() => import('./BabyCareSection'));
const Glossary = lazy(() => import('./Glossary'));

const PrenatalCareApp = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [currentSection, setCurrentSection] = useState('home');
  const [healthSubSection, setHealthSubSection] = useState<string | undefined>(undefined);
  const [initialTabId, setInitialTabId] = useState<string | undefined>(undefined);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [disclaimerExpanded, setDisclaimerExpanded] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('kin-dark-mode');
    return saved ? JSON.parse(saved) : false;
  });
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [fontSize, setFontSize] = useState(() => {
    const saved = localStorage.getItem('kin-font-size');
    return saved ? parseInt(saved) : 100; // 100 = 100% = normal
  });
  const [isHighContrast, setIsHighContrast] = useState(() => {
    const saved = localStorage.getItem('kin-high-contrast');
    return saved ? JSON.parse(saved) : false;
  });

  // Persist dark mode preference
  useEffect(() => {
    localStorage.setItem('kin-dark-mode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  // Persist font size preference
  useEffect(() => {
    localStorage.setItem('kin-font-size', fontSize.toString());
  }, [fontSize]);

  // Persist high contrast preference
  useEffect(() => {
    localStorage.setItem('kin-high-contrast', JSON.stringify(isHighContrast));
  }, [isHighContrast]);

  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const languages: Record<string, { name: string; voice: string }> = {
    en: { name: 'English', voice: 'en-US' },
    es: { name: 'Español', voice: 'es-MX' },
    so: { name: 'Somali', voice: 'so-SO' },
    quc: { name: "K'iche'", voice: 'es-GT' }
  };

  const content: Record<string, Record<string, { title: string; text: string }>> = {
    home: {
      en: {
        title: 'Welcome to Kin',
        text: 'Prenatal care is the healthcare you receive during pregnancy. It includes regular checkups, tests, and guidance to help ensure you and your baby stay healthy. Taking care of yourself during pregnancy is one of the best things you can do for your baby. This guide will help you understand important steps for a healthy pregnancy.'
      },
      es: {
        title: 'Bienvenida a Kin',
        text: 'El cuidado prenatal es la atención médica que recibe durante el embarazo. Incluye chequeos regulares, pruebas y orientación para ayudar a garantizar que usted y su bebé se mantengan saludables. Cuidarse durante el embarazo es una de las mejores cosas que puede hacer por su bebé. Esta guía le ayudará a entender los pasos importantes para un embarazo saludable.'
      },
      so: {
        title: 'Ku soo dhawoow Kin',
        text: 'Daryeelka uurka waa daryeelka caafimaad ee aad hesho inta aad uurka leedahay. Waxaa ku jira baaritaano joogto ah, tijaabooyin, iyo hagid si loo hubiyo in adiga iyo ilmahaagu caafimaad qabaan. Is daryeelka inta aad uur leedahay waa waxyaalaha ugu fiican ee aad u samayn karto ilmahaaga. Hagahan wuxuu kaa caawin doonaa inaad fahamto tallaabooyin muhiim ah oo lagu helo uur caafimaad qaba.'
      },
      quc: {
        title: 'Utz apetik pa Kin',
        text: 'Ri ruchuqa qijol are ri chakunem richin achanik ri nakul pa ri qijol. Kuya tzetoj jantape, taq chak, xukem toqob richin yatchajij at xukem ri akal. Chakun awib chi awech pa ri qijol are jun chi ke ri utz taq jastaq yeatäj chi re ri akal. We wuj re xtokow che awe richin yeaqij ri rajawaxik taq beyal richin jun utz qijol.'
      }
    },
    health: {
      en: {
        title: 'Health During Pregnancy',
        text: `Eating a balanced diet is crucial for a healthy pregnancy. Here's what to eat each day:

**Fruits: 2 Cups**
*   What counts as a cup: 1 cup of fresh, frozen, or canned fruit; 1/2 cup of dried fruit; or 4 fl oz (1/2 cup) of 100% fruit juice.
*   Examples: 1 small apple, 1 large banana, 8 large strawberries, or 32 seedless grapes equals about 1 cup.
*   Choose fresh fruits, and get plenty of citrus, melons, and berries rich in vitamin C. Avoid juice with added sugar.

**Vegetables: 2.5-3 Cups**
*   What counts as a cup: 1 cup of raw or cooked vegetables; 2 cups of raw leafy greens; or 1 cup of cooked beans and peas.
*   Examples: 1 large ear of corn, 1 large baked sweet potato, 2 medium carrots, or 1 large raw tomato equals about 1 cup.
*   Try to eat plenty of leafy green veggies like spinach, kale, and broccoli.

**Grains: 6-8 Ounces**
*   What counts as an ounce: 1 slice of bread; 1 cup of cold cereal; or 1/2 cup of cooked rice, pasta, or cereal.
*   Examples: 1 small tortilla, 1 pancake (4.5 inches), or 3 cups of popcorn equals about 1 ounce.
*   Make half your grains whole grains. They have folic acid and iron.

**Protein: 5.5-6.5 Ounces**
*   What counts as an ounce: 1 oz of lean meat, poultry, or seafood; 1 egg; 1/4 cup of cooked beans or peas; or 1/2 ounce of nuts or seeds.
*   Examples: A small chicken breast is about 3 oz, a small hamburger is 2-3 oz, and a salmon steak is 4-6 oz. 12 almonds or 24 pistachios equals 1/2 ounce.
*   Do not eat shark, swordfish, king mackerel, or tilefish. Limit white (albacore) tuna to no more than 6 ounces per week.

**Dairy: 3 Cups**
*   What counts as a cup: 1 cup of milk, yogurt, or pudding; 2 cups of cottage cheese; 1.5 oz of hard cheese; or 2 oz of American cheese.
*   Examples: 1 small container (6 oz) of yogurt counts as 3/4 cup. 1 string cheese counts as about 1/2 cup.
*   Move to low-fat dairy. Avoid soft cheeses like brie, feta, and queso fresco unless pasteurized.

---

**Weight Gain During Pregnancy**
*   **Normal weight women (BMI 18.5-24.9):** Gain 25-35 pounds total.
*   **Overweight women (BMI 25-29.9):** Gain 15-25 pounds total.
*   **Underweight women (BMI less than 18.5):** Gain 28-40 pounds total.
*   Generally, gain 1-4 lbs total in the first 3 months, then about 1 lb per week from months 4-9.

---

**Drink Plenty of Water**
*   Aim for 8-12 cups (64-96 ounces) of water each day.
*   Water helps form amniotic fluid, carry nutrients to your baby, and prevent constipation.
*   **Limit or avoid** drinks with added sugar like soda, sweet tea, and fruit drinks.
*   **Limit caffeine** to less than 200 mg per day (about one 12 oz cup of coffee). Too much caffeine may increase risk of miscarriage.

---

**TAKE YOUR PRENATAL VITAMIN EVERY DAY**
*   **This is extremely important!** Prenatal vitamins contain folic acid, which helps prevent serious birth defects of the brain and spine.
*   Start taking prenatal vitamins as soon as you know you are pregnant, or even before.
*   Take your vitamin at the same time each day to help you remember.
*   **If you cannot afford prenatal vitamins, please tell your healthcare provider at your visit. They can give you a prescription so you can get them at low or no cost.**

---

**DO NOT USE During Pregnancy**
*   **Tobacco/Smoking:** Smoking increases risk of premature birth, low birth weight, and sudden infant death syndrome (SIDS). This includes cigarettes, vaping, and secondhand smoke.
*   **Alcohol:** There is NO safe amount of alcohol during pregnancy. Alcohol can cause fetal alcohol spectrum disorders and lifelong problems for your baby.
*   **Marijuana:** Using marijuana during pregnancy can affect your baby's brain development. It is not safe even if it is legal in your state.
*   **Illicit drugs:** Cocaine, heroin, methamphetamine, and other drugs can cause serious harm to your baby including premature birth, birth defects, and withdrawal symptoms after birth.
*   If you need help quitting any substance, please talk to your healthcare provider. They can help you without judgment.

---

**Calories:** You only need about 300 extra calories per day during pregnancy. Make them count with whole foods from the five food groups.`
      },
      es: {
        title: 'Comer bien durante el embarazo',
        text: `Comer una dieta balanceada es crucial para un embarazo saludable. Esto es lo que debe comer cada día:

**Frutas: 2 tazas**
*   Qué cuenta como una taza: 1 taza de fruta fresca, congelada o enlatada; 1/2 taza de frutos secos; o 4 onzas líquidas (1/2 taza) de jugo 100% de fruta.
*   Ejemplos: 1 manzana pequeña, 1 plátano grande, 8 fresas grandes, o 32 uvas sin semilla equivalen a aproximadamente 1 taza.
*   Elija frutas frescas y consuma muchas frutas cítricas, melones y bayas ricas en vitamina C. Evite los jugos con azúcar agregada.

**Verduras: 2.5-3 tazas**
*   Qué cuenta como una taza: 1 taza de verduras crudas o cocidas; 2 tazas de verduras de hoja verde crudas; o 1 taza de frijoles y guisantes cocidos.
*   Ejemplos: 1 mazorca grande de maíz, 1 camote grande horneado, 2 zanahorias medianas, o 1 tomate grande crudo equivalen a aproximadamente 1 taza.
*   Trate de comer muchas verduras de hoja verde como espinacas, col rizada y brócoli.

**Granos: 6-8 onzas**
*   Qué cuenta como una onza: 1 rebanada de pan; 1 taza de cereal frío; o 1/2 taza de arroz, pasta o cereal cocido.
*   Ejemplos: 1 tortilla pequeña, 1 panqueque (4.5 pulgadas), o 3 tazas de palomitas de maíz equivalen a aproximadamente 1 onza.
*   Haga que la mitad de sus granos sean integrales. Tienen ácido fólico y hierro.

**Proteínas: 5.5-6.5 onzas**
*   Qué cuenta como una onza: 1 onza de carne magra, aves o mariscos; 1 huevo; 1/4 de taza de frijoles o guisantes cocidos; o 1/2 onza de nueces o semillas.
*   Ejemplos: Una pechuga de pollo pequeña pesa aproximadamente 3 onzas, una hamburguesa pequeña pesa entre 2 y 3 onzas, y un filete de salmón pesa entre 4 y 6 onzas. 12 almendras o 24 pistachos equivalen a 1/2 onza.
*   No coma tiburón, pez espada, caballa real ni blanquillo. Limite el atún blanco (albacora) a no más de 6 onzas por semana.

**Lácteos: 3 tazas**
*   Qué cuenta como una taza: 1 taza de leche, yogur o pudín; 2 tazas de requesón; 1.5 onzas de queso duro; o 2 onzas de queso americano.
*   Ejemplos: 1 envase pequeño (6 oz) de yogur cuenta como 3/4 de taza. 1 queso en tiras cuenta como aproximadamente 1/2 taza.
*   Consuma productos lácteos bajos en grasa. Evite los quesos blandos como el brie, feta y queso fresco a menos que estén pasteurizados.

---

**Aumento de peso durante el embarazo**
*   **Mujeres de peso normal (IMC 18.5-24.9):** Aumente de 25 a 35 libras en total.
*   **Mujeres con sobrepeso (IMC 25-29.9):** Aumente de 15 a 25 libras en total.
*   **Mujeres con bajo peso (IMC menor de 18.5):** Aumente de 28 a 40 libras en total.
*   Generalmente, aumente de 1 a 4 libras en total durante los primeros 3 meses, luego aproximadamente 1 libra por semana de los meses 4 al 9.

---

**Beba mucha agua**
*   Trate de beber de 8 a 12 tazas (64-96 onzas) de agua cada día.
*   El agua ayuda a formar el líquido amniótico, llevar nutrientes a su bebé y prevenir el estreñimiento.
*   **Limite o evite** las bebidas con azúcar agregada como refrescos, té dulce y bebidas de frutas.
*   **Limite la cafeína** a menos de 200 mg por día (aproximadamente una taza de 12 oz de café). Demasiada cafeína puede aumentar el riesgo de aborto espontáneo.

---

**TOME SU VITAMINA PRENATAL TODOS LOS DÍAS**
*   **¡Esto es extremadamente importante!** Las vitaminas prenatales contienen ácido fólico, que ayuda a prevenir defectos de nacimiento graves del cerebro y la columna vertebral.
*   Comience a tomar vitaminas prenatales tan pronto como sepa que está embarazada, o incluso antes.
*   Tome su vitamina a la misma hora cada día para ayudarle a recordar.
*   **Si no puede pagar las vitaminas prenatales, por favor dígale a su proveedor de atención médica en su visita. Ellos pueden darle una receta para que pueda obtenerlas a bajo costo o sin costo.**

---

**NO USE durante el embarazo**
*   **Tabaco/Fumar:** Fumar aumenta el riesgo de parto prematuro, bajo peso al nacer y síndrome de muerte súbita del lactante (SMSL). Esto incluye cigarrillos, vapeo y humo de segunda mano.
*   **Alcohol:** NO hay cantidad segura de alcohol durante el embarazo. El alcohol puede causar trastornos del espectro alcohólico fetal y problemas de por vida para su bebé.
*   **Marihuana:** Usar marihuana durante el embarazo puede afectar el desarrollo cerebral de su bebé. No es seguro incluso si es legal en su estado.
*   **Drogas ilícitas:** La cocaína, heroína, metanfetamina y otras drogas pueden causar daños graves a su bebé, incluyendo parto prematuro, defectos de nacimiento y síntomas de abstinencia después del nacimiento.
*   Si necesita ayuda para dejar cualquier sustancia, por favor hable con su proveedor de atención médica. Ellos pueden ayudarle sin juzgarle.

---

**Calorías:** Solo necesita unas 300 calorías adicionales al día durante el embarazo. Haga que cuenten con alimentos integrales de los cinco grupos de alimentos.`
      },
      so: {
        title: 'Si sax ah u cun xilliga uurka',
        text: `Cunista cunto isku dheelitiran ayaa muhiim u ah uur caafimaad qaba. Waa tan waxa la cuno maalin kasta:

**Miraha: 2 koob**
*   Maxaa lagu tiriyaa koob: 1 koob oo miro daray ah, barafaysan, ama qasacadaysan; 1/2 koob oo miro la qalajiyey; ama 4 fl oz (1/2 koob) oo ah 100% casiir miro.
*   Tusaalooyin: 1 tufaax yar, 1 moos weyn, 8 strawberry weyn, ama 32 canab aan iniin lahayn waxay u dhigmaan qiyaastii 1 koob.
*   Dooro miro daray ah, oo hel liin badan, qaraha, iyo berry qani ku ah fiitamiin C. Iska ilaali casiirka lagu daray sonkorta.

**Khudaarta: 2.5-3 koob**
*   Maxaa lagu tiriyaa koob: 1 koob oo khudaar cayriin ah ama la kariyey; 2 koob oo cagaar caleen cayriin ah; ama 1 koob oo digir iyo digir la kariyey.
*   Tusaalooyin: 1 galeen weyn, 1 baradho macaan weyn oo la dubay, 2 karooto dhexdhexaad ah, ama 1 yaanyo weyn oo cayriin waxay u dhigmaan qiyaastii 1 koob.
*   Isku day inaad cunto khudaar badan oo cagaar caleen ah sida isbinaash, kale, iyo broccoli.

**Badarka: 6-8 wiqiyadood**
*   Maxaa lagu tiriyaa wiqiyadood: 1 xaleef rooti ah; 1 koob oo badar qabow ah; ama 1/2 koob oo bariis la kariyey, baasto, ama badar ah.
*   Tusaalooyin: 1 tortilla yar, 1 pancake (4.5 inches), ama 3 koob popcorn waxay u dhigmaan qiyaastii 1 wiqiyadood.
*   Ka dhig badh badarkaaga badar dhan. Waxay leeyiin folic acid iyo bir.

**Burototiin: 5.5-6.5 wiqiyadood**
*   Maxaa lagu tiriyaa wiqiyadood: 1 wiqiyadood oo hilib caato ah, digaag, ama cunto badeed; 1 ukun; 1/4 koob oo digir ama digir la kariyey; ama 1/2 wiqiyadood oo laws ama iniin ah.
*   Tusaalooyin: Naas digaag oo yar waa qiyaastii 3 wiqiyadood, hamburger yarna waa 2-3 wiqiyadood, hilibka salmonka waa 4-6 wiqiyadood. 12 almond ama 24 pistachio waxay u dhigmaan 1/2 wiqiyadood.
*   Ha cunin shark, swordfish, king mackerel, ama tilefish. Ku xaddid tuna-da cad (albacore) wax aan ka badnayn 6 wiqiyadood toddobaadkii.

**Caanaha: 3 koob**
*   Maxaa lagu tiriyaa koob: 1 koob oo caano ah, yogurt, ama bur saliid ah; 2 koob oo jiis cariish ah; 1.5 wiqiyadood oo jiis adag ah; ama 2 wiqiyadood oo jiis Mareykan ah.
*   Tusaalooyin: 1 weelka yar (6 oz) ee yogurt waxay u dhigmaan 3/4 koob. 1 jiis xarig ah wuxuu u dhigmaa qiyaastii 1/2 koob.
*   U gudub caano dufan yar. Iska ilaali jiiska jilicsan sida brie, feta, iyo queso fresco haddii aan la pasteurize gareyn.

---

**Miisaanka oo kordha xilliga uurka**
*   **Dumarka miisaankoodu caadiga yahay (BMI 18.5-24.9):** Ku dar 25-35 rodol wadarta.
*   **Dumarka miisaankoodu badan yahay (BMI 25-29.9):** Ku dar 15-25 rodol wadarta.
*   **Dumarka miisaankoodu hooseeyo (BMI ka yar 18.5):** Ku dar 28-40 rodol wadarta.
*   Guud ahaan, ku dar 1-4 rodol wadarta 3-da bilood ee hore, ka dibna qiyaastii 1 rodol toddobaadkii bilaha 4-9.

---

**Cab biyo badan**
*   Isku day 8-12 koob (64-96 wiqiyadood) oo biyo ah maalin kasta.
*   Biyuhu waxay ka caawiyaan samaynta dheecaanka amniotic-ka, qaadista nafaqooyinka ilmahaaga, iyo ka hortagga calool adkaanta.
*   **Xaddid ama iska ilaali** cabitaannada lagu daray sonkorta sida soda, shaaha macaan, iyo cabitaannada miraha.
*   **Xaddid kafeega** wax ka yar 200 mg maalintii (qiyaastii 1 koob 12 oz oo kafeeto ah). Kafee badan ayaa kordhin kara halista dhicista.

---

**QAADO FIITAMIINKAAGA UURKA MAALIN KASTA**
*   **Tani aad ayay muhiim u tahay!** Fiitamiinada uurka waxay ka kooban yihiin folic acid, kaas oo ka caawinaya ka hortagga ciladaha dhalashada ee aadka u daran ee maskaxda iyo laf dhabarka.
*   Bilow qaadashada fiitamiinada uurka isla markaas aad ogaato inaad uur leedahay, ama xitaa ka hor.
*   Qaado fiitamiinkaaga isla waqtiga maalin kasta si ay kuu caawiso xusuusashada.
*   **Haddii aadan awoodi karin inaad iibsato fiitamiinada uurka, fadlan u sheeg bixiyaha daryeelkaaga caafimaadka booqashadaada. Waxay ku siin karaan wargelin si aad u hesho lacag yar ama bilaash.**

---

**HA ISTICMAALIN xilliga uurka**
*   **Tubaakada/Sigaarka:** Sigaar cabista waxay kordhisaa halista dhalmada horey, culus yahay dhalmada, iyo cudurka dhimashada kediska (SIDS). Tan waxaa ku jira sigaarka, vaping-ka, iyo qiiqda sigaarka.
*   **Khamriga:** MA JIRO xad ammaan ah oo khamri ah xilliga uurka. Khamrigu waxa uu keeni karaa xanuunada fetal alcohol spectrum disorders iyo dhibaatooyin nolosha oo dhan ilmahaaga.
*   **Marijuana-da:** Isticmaalka marijuana-da xilliga uurka waxay saameyn kartaa koboca maskaxda ilmahaaga. Ma ahan mid ammaan ah xitaa haddii ay sharci tahay gobolkaaga.
*   **Daroogada sharci-darra:** Cocaine, heroin, methamphetamine, iyo daroogooyinka kale waxay keeni karaan waxyeello aad u daran ilmahaaga oo ay ku jiraan dhalmada horey, cillado dhalmada, iyo calaamado ka fogaanshiyeed dhalmada ka dib.
*   Haddii aad u baahan tahay caawimaad si aad u joojiso wax kasta oo aad isticmaasho, fadlan la hadal bixiyaha daryeelkaaga caafimaadka. Waxay kaa caawin karaan adiga oo aan lagu xukumin.

---

**Kalooriyeyaasha:** Waxaad u baahan tahay oo keliya qiyaastii 300 oo kaloori oo dheeri ah maalintii xilliga uurka. Ka dhig inay ku xisaabtamaan cuntooyinka oo dhan ee shanta kooxood ee cuntada.`
      },
      quc: {
        title: 'Utz ütz we süq naqaj pa q\'ijol',
        text: `Utz ütz we süq naqaj pa q'ijol chupam jun utziläj b'enäq. Are wa' we k'atzinel chi naqaj pa q'ijol:

**Ruwa k'oj: 2 t'as**
*   Jas kub'an jun t'as: Jun t'as ruwa k'oj xa b'a k'oj, ch'uch', o pa q'as; 1/2 t'as chaqi'j ruwa k'oj; o 4 fl oz (1/2 t'as) 100% ruwa k'oj.
*   K'utb'al: Jun ch'utin mansana, jun nïm plátano, 8 nïm fresa, o 32 uvas achi'el jun t'as.
*   Cha' ruwa k'oj xa b'a k'oj, xuquje' k'ïy limoon, meloon, xuquje' q'eqq'öj richin Vitamin C. Me'aj ruwa k'oj ruk'amon asukal.

**Ichaj: 2.5-3 t'as**
*   Jas kub'an jun t'as: Jun t'as ichaj xa b'a ichaj o t'oq t'oq; 2 t'as xa b'a rax ichaj; o jun t'as t'oq t'oq pwaq xuquje' pis.
*   K'utb'al: Jun nïm hal, jun nïm ch'oq' pora, ka'i' nik'aj zanahoria, o jun nïm tomate achi'el jun t'as.
*   Chab'ana' k'ïy rax ichaj achi'el espinaca, kale, xuquje' brócoli.

**Ixim: 6-8 oons**
*   Jas kub'an jun oons: Jun peraj kaxlanwa; jun t'as ch'uch' ixim; o 1/2 t'as t'oq t'oq arroz, pasta, o ixim.
*   K'utb'al: Jun ch'utin tortilla, jun panqueque (4.5 pulgadas), o oxi' t'as popcorn achi'el jun oons.
*   Chab'ana' nik'aj awixim ronojel ixim. K'o ácido fólico xuquje' ch'ich' chupam.

**Proteína: 5.5-6.5 oons**
*   Jas kub'an jun oons: 1 oons b'aqil ti'ij, äk', o jux; jun saq'om; 1/4 t'as t'oq t'oq pwaq o pis; o 1/2 oons muj o ija'.
*   K'utb'al: Jun ch'utin ru-pecho äk' k'o 3 oons, jun ch'utin hamburguesa k'o 2-3 oons, xuquje' jun filete de salmón k'o 4-6 oons. 12 almendra o 24 pistacho achi'el 1/2 oons.
*   Me'atij shark, pez espada, rey caballa, o blanquillo. Chapimaj atún blanco (albacora) man k'o tä 6 oons pa wuqub'ix.

**Lechej: 3 t'as**
*   Jas kub'an jun t'as: Jun t'as lechej, yogurt, o pudin; 2 t'as requesón; 1.5 oons q'oq'os queso; o 2 oons queso americano.
*   K'utb'al: Jun ch'utin tz'aqat (6 oz) yogurt achi'el 3/4 t'as. Jun queso en tira achi'el 1/2 t'as.
*   B'enäq pa lechej man k'o tä grasa. Me'aj queso jilïl achi'el brie, feta, xuquje' queso fresco we man pasteurizado tä.

---

**Ripïx pa q'ijol**
*   **Ixoqi' e k'o pa ri normal kipïx (BMI 18.5-24.9):** K'ïy 25-35 libras.
*   **Ixoqi' e k'o pa sobrepeso (BMI 25-29.9):** K'ïy 15-25 libras.
*   **Ixoqi' e k'o pa bajo peso (BMI menos de 18.5):** K'ïy 28-40 libras.
*   Pa ronojel, k'ïy 1-4 libras pa ri nab'ey 3 ik', k'a ri' achi'el 1 libra pa wuqub'ix pa ri 4-9 ik'.

---

**K'ama' k'ïy ya'**
*   Chab'ana' 8-12 t'as (64-96 onzas) ya' pa q'ijol.
*   Ri ya' nuto'on chi ri ri amniotic fluid, nuq'asaj ri nafaq chi re ri awal, xuquje' nukumaj ri constipación.
*   **Chapimaj o me'aj** bebidas ruk'amon asukal achi'el soda, ch'uch' té, xuquje' bebidas de fruta.
*   **Chapimaj ri cafeína** menos de 200 mg pa q'ijol (achi'el jun t'as 12 oz de café). K'ïy cafeína nuk'utun k'ax richin ri aborto espontáneo.

---

**K'AMA' RI VITAMINA PRENATAL PA Q'IJOL**
*   **¡Re k'o nim rajawaxik!** Ri vitaminas prenatales k'o ácido fólico chupam, ri nuto'on chi ukumaxik ri defectos de nacimiento nimaq ri cerebro xuquje' columna vertebral.
*   Chatikirisaj kik'amaxik ri vitaminas prenatales yan we nawetamaj chi k'o awal awik'in, o chik chuwäch.
*   K'ama' ri vitamina pa jun ramaj pa q'ijol richin nak'ulu' na'oj.
*   **We man yatikïr ta chak'amaj ri vitaminas prenatales, tabij chi re ri akunanel pa ri atzetoj. Rije' yekwin yeruya' jun receta richin yak'ül chi man k'o tä rajil o man k'o tä.**

---

**ME'AB'EYAJ pa q'ijol**
*   **Tabaco/Sigarro:** Ri sigarro nuk'utun k'ax richin ri parto prematuro, bajo peso al nacer, xuquje' síndrome de muerte súbita del lactante (SMSL). Re nuk'utun ri cigarrillos, vaping, xuquje' humo de segunda mano.
*   **Alcohol:** MAN K'O TA jun utz cantidad de alcohol pa q'ijol. Ri alcohol yatikïr yukisaj trastornos del espectro alcohólico fetal xuquje' k'ax chi re ri awal pa ronojel uk'aslem.
*   **Marijuana:** Ri uk'amaxik marijuana pa q'ijol yatikïr nub'an k'ax chi re ri uk'iyirisaxik ri cerebro ri awal. Man utz tä xane we legal pa ri aestado.
*   **Drogas ilícitas:** Ri cocaína, heroína, metanfetamina, xuquje' ch'aqa' drogas yetikïr yukisaj k'ax nimaq chi re ri awal chi nuk'utun parto prematuro, defectos de nacimiento, xuquje' síntomas de abstinencia chi rij ri alaxïk.
*   We nawajo' to'ïk richin nakirij ri uk'amaxik jun jastäq, tabij chi re ri akunanel. Rije' yetikïr yato'on man ruk' tä tz'iloxik.

---

**Calorías:** Xa k'atzinel achi'el 300 calorías más pa q'ijol. Chab'ana' chi ronojel naqaj pa ri wo'o' grupos de alimentos.`
      }
    },
    appointments: {
      en: {
        title: 'Prenatal Appointments',
        text: 'Attend all your prenatal checkups. These appointments help your healthcare provider monitor your health and your baby\'s development. Tell your provider about any concerns or questions you have. Early and regular prenatal care is very important.'
      },
      es: {
        title: 'Citas Prenatales',
        text: 'Asista a todos sus chequeos prenatales. Estas citas ayudan a su proveedor de atención médica a monitorear su salud y el desarrollo de su bebé. Informe a su proveedor sobre cualquier preocupación o pregunta que tenga. El cuidado prenatal temprano y regular es muy importante.'
      },
      so: {
        title: 'Ballamaha uurka',
        text: 'Ka qayb gal dhammaan baaritaanada uurka. Ballamahan waxay ka caawiyaan bixiyaha daryeelka caafimaadkaaga inuu la socdo caafimaadkaaga iyo kobaca ilmahaaga. U sheeg bixiyahaaga wax kasta oo ku walaacsan ama suaalo aad qabto. Daryeelka uurka ee hore iyo mid joogto ah aad ayuu muhiim u yahay.'
      },
      quc: {
        title: 'Taq tzetoj richin qijol',
        text: 'Jat pa ronojel ri atzetoj richin qijol. We taq tzetoj re yetoon ri akunanel richin nril awachanik xukem ri akal. Tabij chi re ri akunanel ronojel ri nikaj awach o taq kutunik ko awik\'in. Ri nabey xukem jantape ruchuqa qijol nim rajawaxik.'
      }
    },
    warning: {
      en: {
        title: 'Warning Signs',
        text: 'Call your healthcare provider right away if you experience: severe headache, blurred vision, severe abdominal pain, vaginal bleeding, decreased baby movement, fever over 100.4 degrees, or unusual swelling. These could be signs of serious problems that need immediate attention.'
      },
      es: {
        title: 'Señales de Advertencia',
        text: 'Llame a su proveedor de atención médica de inmediato si experimenta: dolor de cabeza severo, visión borrosa, dolor abdominal severo, sangrado vaginal, disminución del movimiento del bebé, fiebre superior a 100.4 grados, o hinchazón inusual. Estos podrían ser signos de problemas graves que necesitan atención inmediata.'
      },
      so: {
        title: 'Calaamadaha digniin',
        text: 'Wac bixiyaha daryeelka caafimaadkaaga isla markiiba haddii aad la kulanto: madax xanuun daran, aragga aan cad ahayn, xanuun caloosha oo xun, dhiig ka soo bax farjiga, ilmaha uu dhaqdhaqaaqa yaraaday, qandho ka sarreeya 100.4 heerkulka, ama barar aan caadi ahayn. Kuwani waxay noqon karaan calaamado dhibaatooyin halis ah oo u baahan daweyn degdeg ah.'
      },
      quc: {
        title: 'Taq koxom retal',
        text: 'Chayaa ri akunanel yan we nakulwachij: nim kax jolom, man ütz ta ri awil, nim kax pa pam, kikel, man nuban ta uchukik ri akal, qaqalaj nimaj chuwäch 100.4 grados, o jun man rajawaxik ta upamuxik. We re yalan ko niban kax ri rajawaxik yan chuwäch kachajixik.'
      }
    }
  };

  const sections = [
    { id: 'home', icon: '🏠', label: { en: 'Home', es: 'Inicio', so: 'Guriga', quc: 'Rachoch' } },
    { id: 'appointments', icon: '📅', label: { en: 'Appointments', es: 'Citas', so: 'Ballamo', quc: 'Tzetoj' } },
    { id: 'health', icon: '🍎', label: { en: 'Health', es: 'Salud', so: 'Caafimaad', quc: 'Ruchuqa' } },
    { id: 'mental-health', icon: '🧠', label: { en: 'Mental Health', es: 'Salud Mental', so: 'Caafimaadka Dhimirka', quc: 'Ruchuqa Na\'oj' } },
    { id: 'medications', icon: '💊', label: { en: 'Medications', es: 'Medicamentos', so: 'Daawooyin', quc: 'Kunab\'al' } },
    { id: 'conditions', icon: '📊', label: { en: 'Conditions', es: 'Condiciones', so: 'Xaaladaha', quc: 'Xak\'ab\'' } },
    { id: 'warning', icon: '⚠️', label: { en: 'Warning Signs', es: 'Señales', so: 'Digniin', quc: 'Koxom' } },
    { id: 'labor', icon: '💪', label: { en: 'Labor & Delivery', es: 'Parto', so: 'Fooshka', quc: 'Alaxïk' } },
    { id: 'birth-control', icon: '🗓️', label: { en: 'Birth Control', es: 'Anticonceptivos', so: 'Xakameynta Dhalmada', quc: 'Xakameynta Alaxïk' } },
    { id: 'breastfeeding', icon: '🤱', label: { en: 'Feeding', es: 'Alimentación', so: 'Quudinta', quc: 'Wa\'ïk' } },
    { id: 'baby-care', icon: '👶', label: { en: 'Baby Care', es: 'Cuidado del Bebé', so: 'Daryeelka Ilmaha', quc: 'Ruchuqa Ak\'al' } },
    { id: 'resources', icon: '📋', label: { en: 'Resources', es: 'Recursos', so: 'Kheyraadka', quc: 'Tob\'anik' } }
  ];

  // Track visit and session on mount
  useEffect(() => {
    trackVisit();
    trackSessionStart();
    trackSectionView('home');
    trackLanguageUsage('en');
    trackEvent('page_view', { section: 'home', language: 'en' });

    // Track session end when user leaves
    const handleBeforeUnload = () => {
      trackSessionEnd();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Handle scroll for back to top button
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle language change
  const handleLanguageChange = (code: string) => {
    stopSpeaking();
    setCurrentLanguage(code);
    trackLanguageUsage(code);
    trackEvent('language_change', { language: code });
  };

  // Handle section change
  const handleSectionChange = (sectionId: string) => {
    stopSpeaking();
    setCurrentSection(sectionId);
    setHealthSubSection(undefined); // Reset subsection when changing sections
    setInitialTabId(undefined); // Reset tab when changing sections
    trackSectionView(sectionId);
    trackEvent('section_view', { section: sectionId, language: currentLanguage });
  };

  // Handle search navigation
  const handleSearchNavigate = (section: string, subSection?: string, tabId?: string) => {
    stopSpeaking();
    setCurrentSection(section);
    if (section === 'health' && subSection) {
      setHealthSubSection(subSection);
    } else {
      setHealthSubSection(undefined);
    }
    // Set the initial tab ID for sections with tabs
    setInitialTabId(tabId);
    trackSectionView(section);
    trackEvent('search_navigate', { section, subSection: subSection || '', tabId: tabId || '', language: currentLanguage });
  };

  // Handle text-to-speech
  const handleTextToSpeech = () => {
    if (isSpeaking) {
      stopSpeaking();
    } else {
      speakText(currentContent.text, currentLanguage);
      trackTextToSpeech(currentSection, currentLanguage);
      trackEvent('text_to_speech', { section: currentSection, language: currentLanguage });
    }
  };

  // Find the best available voice for a language
  const getBestVoice = (langCode: string): SpeechSynthesisVoice | null => {
    const voices = window.speechSynthesis.getVoices();
    const targetLang = languages[langCode]?.voice || 'en-US';

    // Preferred voice names - prioritizing soft, warm female voices
    const preferredVoices: Record<string, string[]> = {
      'en-US': [
        'Ava',           // iOS - soft, warm female voice
        'Allison',       // iOS - gentle female voice
        'Siri Female',   // iOS Siri
        'Victoria',      // macOS - warm female
        'Fiona',         // Scottish - very soft and gentle
        'Tessa',         // South African - warm tone
        'Karen',         // Australian - friendly
        'Moira',         // Irish - soft and warm
        'Samantha',      // iOS default female
        'Microsoft Aria', // Windows 11 - natural female
        'Google UK English Female', // Chrome - softer than US
        'Google US English Female', // Chrome female
      ],
      'es-MX': ['Paulina', 'Google español', 'Microsoft Sabina', 'Monica', 'Angelica'],
      'es-GT': ['Paulina', 'Google español', 'Microsoft Sabina', 'Monica'],
      'so-SO': ['Google Somali', 'Somali'], // Somali voices are rare, fallback to any
    };

    const preferred = preferredVoices[targetLang] || [];

    // First, try to find a preferred voice
    for (const name of preferred) {
      const voice = voices.find(v => v.name.includes(name) && v.lang.startsWith(targetLang.split('-')[0]));
      if (voice) return voice;
    }

    // Next, prefer female voices that are "natural", "enhanced", or "premium"
    const enhancedFemaleVoice = voices.find(v =>
      v.lang.startsWith(targetLang.split('-')[0]) &&
      (v.name.toLowerCase().includes('female') ||
       v.name.toLowerCase().includes('natural') ||
       v.name.toLowerCase().includes('enhanced'))
    );
    if (enhancedFemaleVoice) return enhancedFemaleVoice;

    // Try any Google voice (usually good quality)
    const googleVoice = voices.find(v =>
      v.lang.startsWith(targetLang.split('-')[0]) &&
      v.name.includes('Google')
    );
    if (googleVoice) return googleVoice;

    // Finally, find any voice matching the language
    const anyVoice = voices.find(v => v.lang.startsWith(targetLang.split('-')[0]));
    if (anyVoice) return anyVoice;

    // For Somali, if no voice found, try Arabic as a closer alternative
    if (langCode === 'so') {
      const arabicVoice = voices.find(v => v.lang.startsWith('ar'));
      if (arabicVoice) return arabicVoice;
    }

    return null;
  };

  const speakText = (text: string, lang: string) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();

      // Ensure voices are loaded (they load asynchronously)
      const speak = () => {
        const utterance = new SpeechSynthesisUtterance(text);

        // Try to get the best voice
        const bestVoice = getBestVoice(lang);
        if (bestVoice) {
          utterance.voice = bestVoice;
        }
        utterance.lang = languages[lang].voice;

        // Faster, more natural rate
        utterance.rate = 1.05;
        utterance.pitch = 1.0;

        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);

        window.speechSynthesis.speak(utterance);
      };

      // Voices may not be loaded yet on first call
      if (window.speechSynthesis.getVoices().length === 0) {
        window.speechSynthesis.onvoiceschanged = speak;
      } else {
        speak();
      }
    }
  };

  const stopSpeaking = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  };

  const currentContent = content[currentSection]?.[currentLanguage] || content['home'][currentLanguage];

  // Show admin dashboard if enabled
  if (showAdmin) {
    return <AdminDashboard onClose={() => setShowAdmin(false)} />;
  }

  return (
    <div
      className={`min-h-screen pb-20 md:pb-4 transition-colors duration-300 ${
        isDarkMode
          ? isHighContrast
            ? 'bg-black'
            : 'bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800'
          : isHighContrast
            ? 'bg-white'
            : 'bg-gradient-to-br from-amber-50 via-blue-50 to-pink-50'
      }`}
      style={{ fontSize: `${fontSize}%` }}
    >
      {/* Sticky Header */}
      <div className={`sticky top-0 z-50 px-4 py-2 backdrop-blur-md border-b transition-colors duration-300 ${
        isDarkMode
          ? isHighContrast ? 'bg-black border-white' : 'bg-gray-900/90 border-gray-700'
          : isHighContrast ? 'bg-white border-black' : 'bg-white/90 border-amber-100'
      }`}>
        <div className="flex items-center justify-between">
          {/* Logo/Title - clickable to go home */}
          <button
            onClick={() => handleSectionChange('home')}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <img src="/logo.png" alt="Kin Logo" className="w-8 h-8 rounded-full" />
            <span className="text-xl font-bold" style={{ color: '#E8B4B8' }}>
              Kin
            </span>
          </button>

          {/* Right side controls */}
          <div className="flex items-center gap-1">
            {/* Font size controls */}
            <div className={`flex items-center rounded-full ${
              isDarkMode ? 'bg-gray-800' : 'bg-gray-100'
            }`}>
              <button
                onClick={() => setFontSize(Math.max(80, fontSize - 10))}
                disabled={fontSize <= 80}
                className={`p-1.5 rounded-full transition-colors disabled:opacity-30 ${
                  isDarkMode
                    ? 'hover:bg-gray-700 text-gray-300'
                    : 'hover:bg-gray-200 text-gray-600'
                }`}
                title="Decrease font size"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className={`text-xs font-medium px-1 min-w-8 text-center ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                A
              </span>
              <button
                onClick={() => setFontSize(Math.min(150, fontSize + 10))}
                disabled={fontSize >= 150}
                className={`p-1.5 rounded-full transition-colors disabled:opacity-30 ${
                  isDarkMode
                    ? 'hover:bg-gray-700 text-gray-300'
                    : 'hover:bg-gray-200 text-gray-600'
                }`}
                title="Increase font size"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {/* High contrast toggle */}
            <button
              onClick={() => setIsHighContrast(!isHighContrast)}
              className={`p-2 rounded-full transition-colors ${
                isHighContrast
                  ? 'bg-yellow-400 text-black'
                  : isDarkMode
                    ? 'hover:bg-gray-700 text-gray-400'
                    : 'hover:bg-gray-100 text-gray-600'
              }`}
              title={isHighContrast ? 'Normal contrast' : 'High contrast'}
            >
              <Contrast className="w-5 h-5" />
            </button>

            {/* Dark mode toggle */}
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className={`p-2 rounded-full transition-colors ${
                isDarkMode
                  ? 'hover:bg-gray-700 text-yellow-400'
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
              title={isDarkMode ? 'Light mode' : 'Dark mode'}
            >
              {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>

            {/* Admin button */}
            <button
              onClick={() => setShowAdmin(true)}
              className={`p-2 rounded-full transition-colors ${
                isDarkMode
                  ? 'hover:bg-gray-700 text-gray-400'
                  : 'hover:bg-gray-100 text-gray-400'
              }`}
              title="Admin Dashboard"
            >
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Offline Banner */}
      {isOffline && (
        <div className={`px-4 py-2 flex items-center justify-center gap-2 text-sm ${
          isHighContrast
            ? 'bg-yellow-400 text-black'
            : 'bg-amber-100 text-amber-800'
        }`}>
          <WifiOff className="w-4 h-4" />
          <span>
            {currentLanguage === 'en' && "You're offline. Some features may be limited."}
            {currentLanguage === 'es' && "Estás sin conexión. Algunas funciones pueden estar limitadas."}
            {currentLanguage === 'so' && "Offline ayaad tahay. Qaar ka mid ah sifaha ayaa laga yaabaa inay xaddidan yihiin."}
            {currentLanguage === 'quc' && "Man k'o ta pa k'amaya'l. Jujun taq samaj rik'in jub'a' man xkechaku ta."}
          </span>
        </div>
      )}

      <div className="p-4">
        <div className={`rounded-2xl shadow-lg p-4 md:p-6 mb-4 md:mb-6 border-2 transition-colors duration-300 ${
          isDarkMode
            ? isHighContrast ? 'bg-black border-white' : 'bg-gray-800 border-gray-700'
            : isHighContrast ? 'bg-white border-black' : 'bg-white border-amber-100'
        }`}>
          {/* Logo on left (absolute on desktop), title/languages centered */}
          <div className="relative flex flex-col items-center gap-4 md:gap-6 mb-4">
            {/* Logo - stacked on mobile, absolute left on desktop */}
            <img src="/logo.png" alt="KCH Logo" className="w-20 h-20 md:w-32 md:h-32 rounded-full flex-shrink-0 md:absolute md:left-0 md:top-1/2 md:-translate-y-1/2" />

            {/* Title and language selector - centered with equal padding to account for logo */}
            <div className="text-center w-full md:px-36">
              <div className="text-3xl md:text-5xl font-bold" style={{ color: isHighContrast ? (isDarkMode ? '#FFFFFF' : '#000000') : '#E8B4B8' }}>Kin</div>
              <div className={`text-base md:text-xl font-semibold ${
                isHighContrast
                  ? isDarkMode ? 'text-white' : 'text-black'
                  : isDarkMode ? 'text-amber-400' : 'text-amber-800'
              }`}>Kearny County Hospital</div>
              <div className={`text-sm md:text-lg ${
                isHighContrast
                  ? isDarkMode ? 'text-white' : 'text-black'
                  : isDarkMode ? 'text-amber-300' : 'text-amber-700'
              }`}>
                {currentLanguage === 'en' && 'Pregnancy Care Guide'}
                {currentLanguage === 'es' && 'Guía de Cuidado del Embarazo'}
                {currentLanguage === 'so' && 'Hagaha Daryeelka Uuraysiga'}
                {currentLanguage === 'quc' && "Wuj Rech Uchuq'ab' ri Qijol"}
              </div>
              <div className="flex flex-wrap justify-center gap-1.5 md:gap-2 mt-3 md:mt-4">
                {Object.entries(languages).map(([code, { name }]) => (
                  <button
                    key={code}
                    onClick={() => handleLanguageChange(code)}
                    className={`px-3 py-2 md:px-6 md:py-3 rounded-lg md:rounded-xl font-semibold text-sm md:text-lg transition-all ${
                      currentLanguage === code
                        ? 'bg-gradient-to-r from-blue-400 to-blue-500 text-white shadow-lg scale-105'
                        : isDarkMode
                          ? 'bg-gray-700 text-blue-300 border-2 border-blue-500 hover:border-blue-400'
                          : 'bg-white text-blue-600 border-2 border-blue-200 hover:border-blue-400'
                    }`}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Hero Welcome Section - Only shown on home */}
        {currentSection === 'home' && (
          <>
            {/* Hero Banner */}
            <div className={`rounded-2xl shadow-xl p-4 md:p-6 mb-4 md:mb-6 border-2 relative overflow-hidden transition-colors duration-300 ${
              isDarkMode
                ? 'bg-gradient-to-r from-gray-800 via-slate-800 to-gray-700 border-gray-600'
                : 'bg-gradient-to-r from-amber-50 via-pink-50 to-pink-100 border-pink-200'
            }`}>
              {/* Speaker button - top right */}
              <button
                onClick={() => {
                  const heroText = {
                    en: "Welcome to Kin. Your guide to a healthy pregnancy, birth, and beyond. Find information on prenatal care, what to expect at appointments, warning signs to watch for, and how to care for yourself and your baby.",
                    es: "Bienvenida a Kin. Su guía para un embarazo, parto y más allá saludables. Encuentre información sobre el cuidado prenatal, qué esperar en las citas, señales de advertencia y cómo cuidarse a usted y a su bebé.",
                    so: "Ku soo dhawoow Kin. Hagahaaga uur caafimaad qaba, dhalmada, iyo wixii ka dambeeya. Ka hel macluumaad ku saabsan daryeelka uurka, waxa la filan karayo ballamaha, calaamadaha digniin, iyo sida loo daryeelo naftaada iyo ilmahaaga.",
                    quc: "Utz apetik pa Kin. Ri ato'onel richin jun utz q'ijol, alaxïk, xuquje' chi rij. Tawila' etamab'äl chi rij ri ruchuqa q'ijol, ri nanataj pa taq tzetoj, taq retal koxom, xuquje' jas nachajij awi' xuquje' ri awal."
                  };
                  if (isSpeaking) {
                    stopSpeaking();
                  } else {
                    speakText(heroText[currentLanguage as keyof typeof heroText], currentLanguage);
                    trackTextToSpeech('home-welcome', currentLanguage);
                    trackEvent('text_to_speech', { section: 'home-welcome', language: currentLanguage });
                  }
                }}
                className={`absolute top-3 right-3 z-20 p-3 rounded-full transition-all shadow-lg ${
                  isSpeaking
                    ? 'bg-gradient-to-br from-pink-400 to-pink-500 animate-pulse'
                    : 'bg-gradient-to-br from-blue-400 to-blue-500 hover:shadow-xl'
                }`}
              >
                <Volume2 className="w-6 h-6 text-white" />
              </button>

              {/* Decorative elements - hidden on mobile */}
              <div className={`hidden md:block absolute top-0 right-0 w-32 h-32 rounded-full opacity-20 -translate-y-1/2 translate-x-1/2 ${isDarkMode ? 'bg-pink-400' : 'bg-pink-200'}`}></div>
              <div className={`hidden md:block absolute bottom-0 left-0 w-24 h-24 rounded-full opacity-20 translate-y-1/2 -translate-x-1/2 ${isDarkMode ? 'bg-amber-400' : 'bg-amber-200'}`}></div>

              <div className="relative z-10">
                <div className="text-center mb-3 md:mb-4">
                  <h2 className={`text-2xl md:text-3xl lg:text-4xl font-bold mb-2 md:mb-3 ${isDarkMode ? 'text-amber-300' : 'text-amber-900'}`}>
                    {currentContent.title}
                  </h2>
                  <p className={`text-sm md:text-base leading-relaxed max-w-2xl mx-auto ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {currentLanguage === 'en' && "Your guide to a healthy pregnancy, birth, and beyond. Find information on prenatal care, what to expect at appointments, warning signs to watch for, and how to care for yourself and your baby."}
                    {currentLanguage === 'es' && "Su guía para un embarazo, parto y más allá saludables. Encuentre información sobre el cuidado prenatal, qué esperar en las citas, señales de advertencia y cómo cuidarse a usted y a su bebé."}
                    {currentLanguage === 'so' && "Hagahaaga uur caafimaad qaba, dhalmada, iyo wixii ka dambeeya. Ka hel macluumaad ku saabsan daryeelka uurka, waxa la filan karayo ballamaha, calaamadaha digniin, iyo sida loo daryeelo naftaada iyo ilmahaaga."}
                    {currentLanguage === 'quc' && "Ri ato'onel richin jun utz q'ijol, alaxïk, xuquje' chi rij. Tawila' etamab'äl chi rij ri ruchuqa q'ijol, ri nanataj pa taq tzetoj, taq retal koxom, xuquje' jas nachajij awi' xuquje' ri awal."}
                  </p>
                </div>

                {/* Quick feature highlights */}
                <div className={`flex flex-wrap justify-center items-center gap-3 md:gap-4 text-xs md:text-sm mb-3 md:mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  <span className={`flex items-center gap-1 px-2 py-1 rounded-full ${isDarkMode ? 'bg-gray-700/60' : 'bg-white/60'}`}>
                    <span>🌐</span>
                    {currentLanguage === 'en' && "4 Languages"}
                    {currentLanguage === 'es' && "4 Idiomas"}
                    {currentLanguage === 'so' && "4 Luqadood"}
                    {currentLanguage === 'quc' && "4 Ch'ab'äl"}
                  </span>
                  <span className={`flex items-center gap-1 px-2 py-1 rounded-full ${isDarkMode ? 'bg-gray-700/60' : 'bg-white/60'}`}>
                    <Volume2 className="w-3 h-3 md:w-4 md:h-4" />
                    {currentLanguage === 'en' && "Audio Support"}
                    {currentLanguage === 'es' && "Audio"}
                    {currentLanguage === 'so' && "Cod"}
                    {currentLanguage === 'quc' && "Ch'ab'äl"}
                  </span>
                  <span className={`flex items-center gap-1 px-2 py-1 rounded-full ${isDarkMode ? 'bg-gray-700/60' : 'bg-white/60'}`}>
                    <span>🔍</span>
                    {currentLanguage === 'en' && "Search Topics"}
                    {currentLanguage === 'es' && "Buscar"}
                    {currentLanguage === 'so' && "Raadi"}
                    {currentLanguage === 'quc' && "Tzukuxïk"}
                  </span>
                </div>

              </div>
            </div>

            {/* Search Bar */}
            <SearchBar
              currentLanguage={currentLanguage}
              onNavigate={handleSearchNavigate}
              isDarkMode={isDarkMode}
            />

            {/* Section Navigation Label */}
            <div className="text-center mb-3 md:mb-4">
              <h3 className={`text-lg md:text-xl font-semibold ${isDarkMode ? 'text-amber-400' : 'text-amber-800'}`}>
                {currentLanguage === 'en' && 'Explore Topics'}
                {currentLanguage === 'es' && 'Explorar Temas'}
                {currentLanguage === 'so' && 'Sahami Mawduucyada'}
                {currentLanguage === 'quc' && 'Tatzukuj taq Wuj'}
              </h3>
            </div>
          </>
        )}

        {/* Search Bar - shown when not on home */}
        {currentSection !== 'home' && (
          <SearchBar
            currentLanguage={currentLanguage}
            onNavigate={handleSearchNavigate}
            isDarkMode={isDarkMode}
          />
        )}

        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-4 mb-4 md:mb-6">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => handleSectionChange(section.id)}
              className={`p-2 md:p-4 lg:p-6 rounded-xl md:rounded-2xl transition-all shadow-md md:shadow-lg ${
                currentSection === section.id
                  ? isHighContrast
                    ? 'bg-yellow-400 scale-105 border-2 border-black'
                    : 'bg-gradient-to-br from-amber-200 to-pink-200 scale-105 border-2 border-amber-300'
                  : isDarkMode
                    ? isHighContrast
                      ? 'bg-black hover:shadow-xl border-2 border-white'
                      : 'bg-gray-800 hover:shadow-xl border-2 border-gray-600 hover:border-gray-500'
                    : isHighContrast
                      ? 'bg-white hover:shadow-xl border-2 border-black'
                      : 'bg-white hover:shadow-xl border-2 border-amber-100'
              }`}
            >
              <div className="text-2xl md:text-3xl lg:text-4xl mb-1 md:mb-2">{section.icon}</div>
              <div className={`text-xs md:text-sm lg:text-base font-semibold leading-tight ${
                currentSection === section.id
                  ? isHighContrast ? 'text-black' : 'text-amber-900'
                  : isHighContrast
                    ? isDarkMode ? 'text-white' : 'text-black'
                    : isDarkMode ? 'text-amber-300' : 'text-amber-900'
              }`}>
                {section.label[currentLanguage as keyof typeof section.label]}
              </div>
            </button>
          ))}
        </div>

        {currentSection !== 'home' && (
        <div className={`rounded-2xl shadow-xl p-4 md:p-8 border-2 transition-colors duration-300 ${
          isDarkMode
            ? isHighContrast ? 'bg-black border-white' : 'bg-gray-800 border-gray-700'
            : isHighContrast ? 'bg-white border-black' : 'bg-white border-pink-100'
        }`}>
          <Suspense fallback={<SkeletonLoader isDarkMode={isDarkMode} />}>
          {currentSection === 'health' ? (
            <HealthSection
              currentLanguage={currentLanguage}
              onSpeak={(text) => {
                speakText(text, currentLanguage);
                trackTextToSpeech(currentSection, currentLanguage);
                trackEvent('text_to_speech', { section: currentSection, language: currentLanguage });
              }}
              onStopSpeaking={stopSpeaking}
              isSpeaking={isSpeaking}
              initialTabId={healthSubSection || initialTabId}
            />
          ) : currentSection === 'labor' ? (
            <LaborDeliverySection
              currentLanguage={currentLanguage}
              onSpeak={(text) => {
                speakText(text, currentLanguage);
                trackTextToSpeech(currentSection, currentLanguage);
                trackEvent('text_to_speech', { section: currentSection, language: currentLanguage });
              }}
              onStopSpeaking={stopSpeaking}
              isSpeaking={isSpeaking}
              initialTabId={initialTabId}
            />
          ) : currentSection === 'warning' ? (
            <WarningSignsSection
              currentLanguage={currentLanguage}
              onSpeak={(text) => {
                speakText(text, currentLanguage);
                trackTextToSpeech(currentSection, currentLanguage);
                trackEvent('text_to_speech', { section: currentSection, language: currentLanguage });
              }}
              onStopSpeaking={stopSpeaking}
              isSpeaking={isSpeaking}
              initialTabId={initialTabId}
            />
          ) : currentSection === 'mental-health' ? (
            <MentalHealthSection
              currentLanguage={currentLanguage}
              onSpeak={(text) => {
                speakText(text, currentLanguage);
                trackTextToSpeech(currentSection, currentLanguage);
                trackEvent('text_to_speech', { section: currentSection, language: currentLanguage });
              }}
              onStopSpeaking={stopSpeaking}
              isSpeaking={isSpeaking}
              initialTabId={initialTabId}
            />
          ) : currentSection === 'appointments' ? (
            <AppointmentsSection
              currentLanguage={currentLanguage}
              onSpeak={(text) => {
                speakText(text, currentLanguage);
                trackTextToSpeech(currentSection, currentLanguage);
                trackEvent('text_to_speech', { section: currentSection, language: currentLanguage });
              }}
              onStopSpeaking={stopSpeaking}
              isSpeaking={isSpeaking}
              speakText={speakText}
            />
          ) : currentSection === 'conditions' ? (
            <ConditionsSection
              currentLanguage={currentLanguage}
              onSpeak={(text) => {
                speakText(text, currentLanguage);
                trackTextToSpeech(currentSection, currentLanguage);
                trackEvent('text_to_speech', { section: currentSection, language: currentLanguage });
              }}
              onStopSpeaking={stopSpeaking}
              isSpeaking={isSpeaking}
              initialTabId={initialTabId}
            />
          ) : currentSection === 'medications' ? (
            <MedicationsSection
              currentLanguage={currentLanguage}
              onSpeak={(text) => {
                speakText(text, currentLanguage);
                trackTextToSpeech(currentSection, currentLanguage);
                trackEvent('text_to_speech', { section: currentSection, language: currentLanguage });
              }}
              onStopSpeaking={stopSpeaking}
              isSpeaking={isSpeaking}
              initialTabId={initialTabId}
            />
          ) : currentSection === 'birth-control' ? (
            <BirthControlSection
              currentLanguage={currentLanguage}
              onSpeak={(text) => {
                speakText(text, currentLanguage);
                trackTextToSpeech(currentSection, currentLanguage);
                trackEvent('text_to_speech', { section: currentSection, language: currentLanguage });
              }}
              onStopSpeaking={stopSpeaking}
              isSpeaking={isSpeaking}
            />
          ) : currentSection === 'resources' ? (
            <ResourcesSection
              currentLanguage={currentLanguage}
              onSpeak={(text) => {
                speakText(text, currentLanguage);
                trackTextToSpeech(currentSection, currentLanguage);
                trackEvent('text_to_speech', { section: currentSection, language: currentLanguage });
              }}
              onStopSpeaking={stopSpeaking}
              isSpeaking={isSpeaking}
            />
          ) : currentSection === 'breastfeeding' ? (
            <BreastfeedingSection
              currentLanguage={currentLanguage}
              onSpeak={(text) => {
                speakText(text, currentLanguage);
                trackTextToSpeech(currentSection, currentLanguage);
                trackEvent('text_to_speech', { section: currentSection, language: currentLanguage });
              }}
              onStopSpeaking={stopSpeaking}
              isSpeaking={isSpeaking}
              initialTabId={initialTabId}
            />
          ) : currentSection === 'baby-care' ? (
            <BabyCareSection
              currentLanguage={currentLanguage}
              onSpeak={(text) => {
                speakText(text, currentLanguage);
                trackTextToSpeech(currentSection, currentLanguage);
                trackEvent('text_to_speech', { section: currentSection, language: currentLanguage });
              }}
              onStopSpeaking={stopSpeaking}
              isSpeaking={isSpeaking}
              initialTabId={initialTabId}
            />
          ) : currentSection !== 'home' ? (
            <>
              <div className="flex items-start justify-between mb-6">
                <h2 className="text-3xl font-bold text-amber-900 flex-1">
                  {currentContent.title}
                </h2>
                <button
                  onClick={handleTextToSpeech}
                  className={`p-4 rounded-full transition-all shadow-lg ${
                    isSpeaking
                      ? 'bg-gradient-to-br from-pink-400 to-pink-500 animate-pulse'
                      : 'bg-gradient-to-br from-blue-400 to-blue-500 hover:shadow-xl'
                  }`}
                >
                  <Volume2 className="w-8 h-8 text-white" />
                </button>
              </div>

              <p className="text-xl leading-relaxed text-gray-700 mb-6">
                {currentContent.text}
              </p>
            </>
          ) : null}
          </Suspense>
        </div>
        )}

        {/* Glossary - Medical Terms (only on home and resources pages) */}
        {(currentSection === 'home' || currentSection === 'resources') && (
          <div className="mt-4 md:mt-6">
            <Suspense fallback={
              <div className={`animate-pulse rounded-xl p-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                <div className={`h-6 w-32 rounded mb-3 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
                <div className="flex gap-2">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className={`h-8 w-20 rounded-full ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
                  ))}
                </div>
              </div>
            }>
              <Glossary
                currentLanguage={currentLanguage}
                speakText={speakText}
              />
            </Suspense>
          </div>
        )}

        {/* Why Kin - Near footer on home page only */}
        {currentSection === 'home' && (
          <details className="mt-4 md:mt-6 bg-white rounded-2xl shadow-lg border-2 border-amber-100 group">
            <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
              <div className="flex items-center gap-2 md:gap-3">
                <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-amber-500" />
                <h3 className="text-base md:text-lg font-bold text-amber-800">
                  {currentLanguage === 'en' && 'Why the name "Kin"?'}
                  {currentLanguage === 'es' && '¿Por qué el nombre "Kin"?'}
                  {currentLanguage === 'so' && 'Maxaa magaca "Kin"?'}
                  {currentLanguage === 'quc' && '¿Jas ri ub\'i\' "Kin"?'}
                </h3>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    const whyKinText = {
                      en: 'Why the name Kin? The definition of kin is one\'s family and relations. At Kearny County Hospital, part of our mission is to serve our patients as if they were a part of our own family. Many of our patients are exactly that — family and friends. Just as family members share tips and guidance during pregnancy, this website is here to give you the knowledge you need for a happy and healthy pregnancy, labor and delivery, and start of motherhood.',
                      es: '¿Por qué el nombre Kin? La definición de kin es la familia y las relaciones de uno. En el Hospital del Condado de Kearny, parte de nuestra misión es servir a nuestros pacientes como si fueran parte de nuestra propia familia. Muchos de nuestros pacientes son exactamente eso: familia y amigos. Así como los miembros de la familia comparten consejos y orientación durante el embarazo, este sitio web está aquí para darle el conocimiento que necesita para un embarazo feliz y saludable, parto y el comienzo de la maternidad.',
                      so: 'Maxaa magaca Kin? Qeexitaanka kin waa qoyska iyo qaraabada qofka. Isbitaalka Degmada Kearny, qayb ka mid ah howlgalkeenna waa inaan bukaankeenna u adeegno sidii ay qayb ka yihiin qoyskeenna. Bukaankeenna badankood waa sidaas — qoys iyo saaxiibbo. Sida xubnaha qoyska ay wadaagaan talooyinka iyo hagitaanka xilliga uurka, websaydhkan wuxuu halkan u jooga inuu ku siiyo aqoonta aad u baahan tahay uur farxad leh oo caafimaad qaba, foosha, iyo bilawga hooyadnimada.',
                      quc: '¿Jas ri ub\'i\' Kin? Ri ucholajil kin are ri achb\'il xuquje\' ri awachb\'il. Pa ri Hospital de Kearny County, jun peraj chi re ri qachakunik are chi yeqato\'on ri qayab\'il achi\'el jun peraj chi re ri qachb\'il. K\'ïy chi ke ri qayab\'il are wa\' — achb\'il xuquje\' amigo. Achi\'el ri achb\'il nikiya\' taq pixab\' xuquje\' tob\'anik pa ri q\'ijol, we ruxaq k\'amaya\'l k\'o waral richin nuya\' ri na\'oj nawajo\' richin jun utz xuquje\' kikotem q\'ijol, alaxïk, xuquje\' ri utikirisaxik ri unan.'
                    };
                    if (isSpeaking) {
                      stopSpeaking();
                    } else {
                      speakText(whyKinText[currentLanguage as keyof typeof whyKinText], currentLanguage);
                      trackTextToSpeech('home-why-kin', currentLanguage);
                      trackEvent('text_to_speech', { section: 'home-why-kin', language: currentLanguage });
                    }
                  }}
                  className={`p-2 rounded-full transition-all ${
                    isSpeaking
                      ? 'bg-pink-400 animate-pulse'
                      : 'bg-amber-50 hover:bg-amber-100 border border-amber-200'
                  }`}
                >
                  <Volume2 className="w-5 h-5 text-amber-700" />
                </button>
                <ChevronDown className="w-5 h-5 text-amber-600 transition-transform group-open:rotate-180" />
              </div>
            </summary>
            <div className="px-4 md:px-5 pb-4 md:pb-5 pt-2 border-t border-amber-100">
              <div className="bg-gradient-to-br from-pink-50 to-amber-50 p-3 md:p-4 rounded-xl">
                <p className="text-sm md:text-base leading-relaxed text-gray-700 mb-2">
                  {currentLanguage === 'en' && 'The definition of kin is one\'s family and relations.'}
                  {currentLanguage === 'es' && 'La definición de kin es la familia y las relaciones de uno.'}
                  {currentLanguage === 'so' && 'Qeexitaanka kin waa qoyska iyo qaraabada qofka.'}
                  {currentLanguage === 'quc' && 'Ri ucholajil kin are ri achb\'il xuquje\' ri awachb\'il.'}
                </p>
                <p className="text-sm md:text-base leading-relaxed text-gray-700 mb-2">
                  {currentLanguage === 'en' && 'At Kearny County Hospital, part of our mission is to serve our patients as if they were a part of our own family. Many of our patients are exactly that — family and friends.'}
                  {currentLanguage === 'es' && 'En el Hospital del Condado de Kearny, parte de nuestra misión es servir a nuestros pacientes como si fueran parte de nuestra propia familia. Muchos de nuestros pacientes son exactamente eso: familia y amigos.'}
                  {currentLanguage === 'so' && 'Isbitaalka Degmada Kearny, qayb ka mid ah howlgalkeenna waa inaan bukaankeenna u adeegno sidii ay qayb ka yihiin qoyskeenna. Bukaankeenna badankood waa sidaas — qoys iyo saaxiibbo.'}
                  {currentLanguage === 'quc' && 'Pa ri Hospital de Kearny County, jun peraj chi re ri qachakunik are chi yeqato\'on ri qayab\'il achi\'el jun peraj chi re ri qachb\'il. K\'ïy chi ke ri qayab\'il are wa\' — achb\'il xuquje\' amigo.'}
                </p>
                <p className="text-sm md:text-base leading-relaxed text-gray-700">
                  {currentLanguage === 'en' && 'Just as family members share tips and guidance during pregnancy, this website is here to give you the knowledge you need for a happy and healthy pregnancy, labor & delivery, and start of motherhood.'}
                  {currentLanguage === 'es' && 'Así como los miembros de la familia comparten consejos y orientación durante el embarazo, este sitio web está aquí para darle el conocimiento que necesita para un embarazo feliz y saludable, parto y el comienzo de la maternidad.'}
                  {currentLanguage === 'so' && 'Sida xubnaha qoyska ay wadaagaan talooyinka iyo hagitaanka xilliga uurka, websaydhkan wuxuu halkan u jooga inuu ku siiyo aqoonta aad u baahan tahay uur farxad leh oo caafimaad qaba, foosha, iyo bilawga hooyadnimada.'}
                  {currentLanguage === 'quc' && 'Achi\'el ri achb\'il nikiya\' taq pixab\' xuquje\' tob\'anik pa ri q\'ijol, we ruxaq k\'amaya\'l k\'o waral richin nuya\' ri na\'oj nawajo\' richin jun utz xuquje\' kikotem q\'ijol, alaxïk, xuquje\' ri utikirisaxik ri unan.'}
                </p>
              </div>
            </div>
          </details>
        )}

        {/* Footer Disclaimer - Collapsible on home, short on other pages */}
        {currentSection === 'home' ? (
          <div className={`mt-4 md:mt-6 border-2 rounded-xl overflow-hidden transition-colors duration-300 ${
            isDarkMode ? 'bg-red-900/30 border-red-800' : 'bg-red-50 border-red-200'
          }`}>
            {/* Clickable Tab Header */}
            <button
              onClick={() => setDisclaimerExpanded(!disclaimerExpanded)}
              className={`w-full p-3 md:p-4 flex items-center justify-between transition-colors ${
                isDarkMode ? 'hover:bg-red-900/50' : 'hover:bg-red-100'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-red-600 text-lg">⚠️</span>
                <h3 className={`text-base md:text-lg font-bold ${isDarkMode ? 'text-red-400' : 'text-red-800'}`}>
                  {currentLanguage === 'en' && 'Medical Disclaimer'}
                  {currentLanguage === 'es' && 'Aviso Médico'}
                  {currentLanguage === 'so' && 'Digniin Caafimaad'}
                  {currentLanguage === 'quc' && "Pixab' ri Kunanik"}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const fullDisclaimerText = {
                      en: 'Medical Disclaimer. The information provided in this app is for general educational and informational purposes only and is not intended to be a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of your physician or other qualified healthcare provider with any questions you may have regarding a medical condition or treatment. Never disregard professional medical advice or delay in seeking it because of something you have read in this app. If you think you may have a medical emergency, call your doctor, go to the emergency room, or call 911 immediately. Every pregnancy is unique. The information in this app is intended to provide general guidance, but may not apply to your specific situation. Your healthcare provider knows your medical history and individual circumstances, and is the best source of advice for your prenatal care, labor and delivery, and postpartum needs. This app does not recommend or endorse any specific tests, physicians, products, procedures, or opinions. Reliance on any information provided by this app is solely at your own risk.',
                      es: 'Aviso Médico. La información proporcionada en esta aplicación es solo para fines educativos e informativos generales y no pretende sustituir el consejo médico profesional, el diagnóstico o el tratamiento. Siempre busque el consejo de su médico u otro proveedor de atención médica calificado con cualquier pregunta que pueda tener sobre una condición médica o tratamiento. Nunca ignore el consejo médico profesional ni demore en buscarlo debido a algo que haya leído en esta aplicación. Si cree que puede tener una emergencia médica, llame a su médico, vaya a la sala de emergencias o llame al 911 inmediatamente. Cada embarazo es único. La información en esta aplicación está destinada a proporcionar orientación general, pero puede no aplicarse a su situación específica. Su proveedor de atención médica conoce su historial médico y circunstancias individuales, y es la mejor fuente de consejos para su atención prenatal, parto y necesidades posparto. Esta aplicación no recomienda ni respalda ninguna prueba, médico, producto, procedimiento u opinión específica. La confianza en cualquier información proporcionada por esta aplicación es únicamente bajo su propio riesgo.',
                      so: 'Digniin Caafimaad. Macluumaadka lagu bixiyo app-kan waa ujeedo waxbarasho iyo macluumaad guud oo kaliya mana aha in lagu beddelo talo caafimaad oo xirfad leh, baaritaan, ama daaweyn. Mar walba raadso talada dhakhtarkaaga ama bixiye kale oo caafimaad oo u qalma su\'aalo kasta oo aad qabto oo ku saabsan xaalad caafimaad ama daaweyn. Waligaa ha iska indho tirin talada caafimaadka xirfadda leh ama ha daahin raadinteda waxa aad wax ka akhriday app-kan. Haddii aad u malaynayso inaad qabto xaalad degdeg ah oo caafimaad, wac dhakhtarkaaga, tag qolka gurmadka degdegga ah, ama wac 911 isla markiiba. Uur kasta waa gaar ah. Macluumaadka app-kan waxaa loogu talagalay inuu bixiyo hanuunin guud, laakiin laga yaabo inuusan ku khusayn xaaladaada gaarka ah. Bixiyaha daryeelka caafimaadkaagu wuu yaqaan taariikhda caafimaadkaaga iyo xaaladaha gaarka ah, waana isha ugu fiican ee talada daryeelka uurka, foosha, iyo baahiyaha ka dib dhalmada. App-kani ma soo jeedin ama taageerin tijaabo, dhakhaatiir, alaab, hab, ama ra\'yi gaar ah. Ku kalsoonaanta macluumaad kasta oo ay bixiso app-kani waa khatartaada oo keliya.',
                      quc: "Pixab' ri Kunanik. Ri etamab'äl ya'on pa we app xa xe richin wachib'enïk xuquje' etamab'äl chijun, man are ta chi nub'än chi rij ri upixab' ajkunanel, ri nik'o chi rij ri yab'il, o ri kunanik. Junelïk takanoj ri upixab' ri akunanel o jun chik ajkunanel ruk' ri taq k'utunïk ri k'o chi awech chi rij ri yab'il o kunanik. Junelïk man tamestaj ri upixab' ajkunanel xuquje' man tajalwachij ta ri utzukuxik ruma ri xasik'ij pa we app. We naná chi k'o jun oyowal richin yab'il, tawoyojej ri akunanel, jät pa ri jay richin oyowal, o tawoyojej 911 aninäq. Jujun q'ijol man junam ta. Ri etamab'äl pa we app ya'on richin nuya' to'onik chijun, po rik'in jub'a' man nikajo' ta chi re ri xa awichin at. Ri akunanel retaman ri ak'aslem richin yab'il xuquje' ri awachinem, xuquje' are ri utz chik chi nikiya' pixab' chi re ri achajixik pa q'ijol, alaxïk, xuquje' ri rajawaxik chi rij alaxïk. We app man nub'ij ta o nuya' retal chi re jun tijonïk, akunanel, ch'akulal, b'eyal, o na'oj. Ri ukojik ri etamab'äl ya'on ruma we app xa awichin at ri k'ayewal."
                    };
                    if (isSpeaking) {
                      stopSpeaking();
                    } else {
                      speakText(fullDisclaimerText[currentLanguage as keyof typeof fullDisclaimerText], currentLanguage);
                      trackTextToSpeech('disclaimer-full', currentLanguage);
                      trackEvent('text_to_speech', { section: 'disclaimer-full', language: currentLanguage });
                    }
                  }}
                  className={`p-1.5 rounded-full transition-all ${
                    isSpeaking
                      ? 'bg-pink-400 animate-pulse'
                      : 'bg-white hover:bg-gray-100 shadow-sm border border-red-200'
                  }`}
                >
                  <Volume2 className="w-4 h-4 text-red-600" />
                </button>
                <span className="text-xs text-red-600">
                  {disclaimerExpanded
                    ? (currentLanguage === 'en' ? 'Hide' : currentLanguage === 'es' ? 'Ocultar' : currentLanguage === 'so' ? 'Qari' : 'Tewäx')
                    : (currentLanguage === 'en' ? 'Read' : currentLanguage === 'es' ? 'Leer' : currentLanguage === 'so' ? 'Akhri' : 'Tisik\'ij')}
                </span>
                <ChevronDown className={`w-5 h-5 text-red-600 transition-transform ${disclaimerExpanded ? 'rotate-180' : ''}`} />
              </div>
            </button>

            {/* Expandable Content */}
            {disclaimerExpanded && (
              <div className="p-3 md:p-5 pt-0 border-t border-red-200">
                <div className="text-sm text-gray-700 space-y-3 mt-3">
                  <p>
                    {currentLanguage === 'en' && 'The information provided in this app is for general educational and informational purposes only and is not intended to be a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of your physician or other qualified healthcare provider with any questions you may have regarding a medical condition or treatment.'}
                    {currentLanguage === 'es' && 'La información proporcionada en esta aplicación es solo para fines educativos e informativos generales y no pretende sustituir el consejo médico profesional, el diagnóstico o el tratamiento. Siempre busque el consejo de su médico u otro proveedor de atención médica calificado con cualquier pregunta que pueda tener sobre una condición médica o tratamiento.'}
                    {currentLanguage === 'so' && 'Macluumaadka lagu bixiyo app-kan waa ujeedo waxbarasho iyo macluumaad guud oo kaliya mana aha in lagu beddelo talo caafimaad oo xirfad leh, baaritaan, ama daaweyn. Mar walba raadso talada dhakhtarkaaga ama bixiye kale oo caafimaad oo u qalma su\'aalo kasta oo aad qabto oo ku saabsan xaalad caafimaad ama daaweyn.'}
                    {currentLanguage === 'quc' && "Ri etamab'äl ya'on pa we app xa xe richin wachib'enïk xuquje' etamab'äl chijun, man are ta chi nub'än chi rij ri upixab' ajkunanel, ri nik'o chi rij ri yab'il, o ri kunanik. Junelïk takanoj ri upixab' ri akunanel o jun chik ajkunanel ruk' ri taq k'utunïk ri k'o chi awech chi rij ri yab'il o kunanik."}
                  </p>
                  <p>
                    {currentLanguage === 'en' && 'Never disregard professional medical advice or delay in seeking it because of something you have read in this app. If you think you may have a medical emergency, call your doctor, go to the emergency room, or call 911 immediately.'}
                    {currentLanguage === 'es' && 'Nunca ignore el consejo médico profesional ni demore en buscarlo debido a algo que haya leído en esta aplicación. Si cree que puede tener una emergencia médica, llame a su médico, vaya a la sala de emergencias o llame al 911 inmediatamente.'}
                    {currentLanguage === 'so' && 'Waligaa ha iska indho tirin talada caafimaadka xirfadda leh ama ha daahin raadinteda waxa aad wax ka akhriday app-kan. Haddii aad u malaynayso inaad qabto xaalad degdeg ah oo caafimaad, wac dhakhtarkaaga, tag qolka gurmadka degdegga ah, ama wac 911 isla markiiba.'}
                    {currentLanguage === 'quc' && "Junelïk man tamestaj ri upixab' ajkunanel xuquje' man tajalwachij ta ri utzukuxik ruma ri xasik'ij pa we app. We naná chi k'o jun oyowal richin yab'il, tawoyojej ri akunanel, jät pa ri jay richin oyowal, o tawoyojej 911 aninäq."}
                  </p>
                  <p>
                    {currentLanguage === 'en' && 'Every pregnancy is unique. The information in this app is intended to provide general guidance, but may not apply to your specific situation. Your healthcare provider knows your medical history and individual circumstances, and is the best source of advice for your prenatal care, labor and delivery, and postpartum needs.'}
                    {currentLanguage === 'es' && 'Cada embarazo es único. La información en esta aplicación está destinada a proporcionar orientación general, pero puede no aplicarse a su situación específica. Su proveedor de atención médica conoce su historial médico y circunstancias individuales, y es la mejor fuente de consejos para su atención prenatal, parto y necesidades posparto.'}
                    {currentLanguage === 'so' && 'Uur kasta waa gaar ah. Macluumaadka app-kan waxaa loogu talagalay inuu bixiyo hanuunin guud, laakiin laga yaabo inuusan ku khusayn xaaladaada gaarka ah. Bixiyaha daryeelka caafimaadkaagu wuu yaqaan taariikhda caafimaadkaaga iyo xaaladaha gaarka ah, waana isha ugu fiican ee talada daryeelka uurka, foosha, iyo baahiyaha ka dib dhalmada.'}
                    {currentLanguage === 'quc' && "Jujun q'ijol man junam ta. Ri etamab'äl pa we app ya'on richin nuya' to'onik chijun, po rik'in jub'a' man nikajo' ta chi re ri xa awichin at. Ri akunanel retaman ri ak'aslem richin yab'il xuquje' ri awachinem, xuquje' are ri utz chik chi nikiya' pixab' chi re ri achajixik pa q'ijol, alaxïk, xuquje' ri rajawaxik chi rij alaxïk."}
                  </p>
                  <p>
                    {currentLanguage === 'en' && 'This app does not recommend or endorse any specific tests, physicians, products, procedures, or opinions. Reliance on any information provided by this app is solely at your own risk.'}
                    {currentLanguage === 'es' && 'Esta aplicación no recomienda ni respalda ninguna prueba, médico, producto, procedimiento u opinión específica. La confianza en cualquier información proporcionada por esta aplicación es únicamente bajo su propio riesgo.'}
                    {currentLanguage === 'so' && 'App-kani ma soo jeedin ama taageerin tijaabo, dhakhaatiir, alaab, hab, ama ra\'yi gaar ah. Ku kalsoonaanta macluumaad kasta oo ay bixiso app-kani waa khatartaada oo keliya.'}
                    {currentLanguage === 'quc' && "We app man nub'ij ta o nuya' retal chi re jun tijonïk, akunanel, ch'akulal, b'eyal, o na'oj. Ri ukojik ri etamab'äl ya'on ruma we app xa awichin at ri k'ayewal."}
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className={`mt-4 md:mt-6 rounded-xl p-3 md:p-4 shadow border transition-colors duration-300 ${
            isDarkMode ? 'bg-red-900/30 border-red-800' : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-start gap-2 md:gap-3">
              <p className={`flex-1 text-xs md:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {currentLanguage === 'en' && 'The information provided in this app is for general educational and informational purposes only and is not intended to be a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of your physician or other qualified healthcare provider with any questions you may have regarding a medical condition or treatment.'}
                {currentLanguage === 'es' && 'La información proporcionada en esta aplicación es solo para fines educativos e informativos generales y no pretende sustituir el consejo médico profesional, el diagnóstico o el tratamiento. Siempre busque el consejo de su médico u otro proveedor de atención médica calificado con cualquier pregunta que pueda tener sobre una condición médica o tratamiento.'}
                {currentLanguage === 'so' && 'Macluumaadka lagu bixiyo app-kan waa ujeedo waxbarasho iyo macluumaad guud oo kaliya mana aha in lagu beddelo talo caafimaad oo xirfad leh, baaritaan, ama daaweyn. Mar walba raadso talada dhakhtarkaaga ama bixiye kale oo caafimaad oo u qalma su\'aalo kasta oo aad qabto oo ku saabsan xaalad caafimaad ama daaweyn.'}
                {currentLanguage === 'quc' && "Ri etamab'äl ya'on pa we app xa xe richin wachib'enïk xuquje' etamab'äl chijun, man are ta chi nub'än chi rij ri upixab' ajkunanel, ri nik'o chi rij ri yab'il, o ri kunanik. Junelïk takanoj ri upixab' ri akunanel o jun chik ajkunanel ruk' ri taq k'utunïk ri k'o chi awech chi rij ri yab'il o kunanik."}
              </p>
              <button
                onClick={() => {
                  const shortDisclaimerText = {
                    en: 'The information provided in this app is for general educational and informational purposes only and is not intended to be a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of your physician or other qualified healthcare provider with any questions you may have regarding a medical condition or treatment.',
                    es: 'La información proporcionada en esta aplicación es solo para fines educativos e informativos generales y no pretende sustituir el consejo médico profesional, el diagnóstico o el tratamiento. Siempre busque el consejo de su médico u otro proveedor de atención médica calificado con cualquier pregunta que pueda tener sobre una condición médica o tratamiento.',
                    so: 'Macluumaadka lagu bixiyo app-kan waa ujeedo waxbarasho iyo macluumaad guud oo kaliya mana aha in lagu beddelo talo caafimaad oo xirfad leh, baaritaan, ama daaweyn. Mar walba raadso talada dhakhtarkaaga ama bixiye kale oo caafimaad oo u qalma su\'aalo kasta oo aad qabto oo ku saabsan xaalad caafimaad ama daaweyn.',
                    quc: "Ri etamab'äl ya'on pa we app xa xe richin wachib'enïk xuquje' etamab'äl chijun, man are ta chi nub'än chi rij ri upixab' ajkunanel, ri nik'o chi rij ri yab'il, o ri kunanik. Junelïk takanoj ri upixab' ri akunanel o jun chik ajkunanel ruk' ri taq k'utunïk ri k'o chi awech chi rij ri yab'il o kunanik."
                  };
                  if (isSpeaking) {
                    stopSpeaking();
                  } else {
                    speakText(shortDisclaimerText[currentLanguage as keyof typeof shortDisclaimerText], currentLanguage);
                    trackTextToSpeech('disclaimer-short', currentLanguage);
                    trackEvent('text_to_speech', { section: 'disclaimer-short', language: currentLanguage });
                  }
                }}
                className={`p-2 rounded-full transition-all flex-shrink-0 ${
                  isSpeaking
                    ? 'bg-pink-400 animate-pulse'
                    : 'bg-white hover:bg-gray-100 shadow-sm border border-red-200'
                }`}
              >
                <Volume2 className="w-5 h-5 text-red-600" />
              </button>
            </div>
          </div>
        )}

        {/* Back to Top Button */}
        {showBackToTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-24 md:bottom-6 right-6 p-2 bg-gradient-to-br from-amber-400 to-pink-400 text-white rounded-full shadow-md opacity-40 hover:opacity-100 hover:shadow-lg hover:scale-110 transition-all z-40"
            aria-label="Back to top"
          >
            <ArrowUp className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Bottom Navigation Bar - Mobile Only */}
      <div className={`fixed bottom-0 left-0 right-0 md:hidden z-50 border-t backdrop-blur-md transition-colors duration-300 ${
        isDarkMode
          ? 'bg-gray-900/95 border-gray-700'
          : 'bg-white/95 border-amber-100'
      }`}>
        <div className="flex items-center justify-around py-2 px-4">
          {/* Home Button */}
          <button
            onClick={() => handleSectionChange('home')}
            className={`flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-all ${
              currentSection === 'home'
                ? 'bg-gradient-to-br from-amber-400 to-pink-400 text-white'
                : isDarkMode
                  ? 'text-gray-400 hover:text-gray-200'
                  : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Home className="w-5 h-5" />
            <span className="text-xs font-medium">
              {currentLanguage === 'en' && 'Home'}
              {currentLanguage === 'es' && 'Inicio'}
              {currentLanguage === 'so' && 'Guriga'}
              {currentLanguage === 'quc' && 'Tikirib\'äl'}
            </span>
          </button>

          {/* Search Button */}
          <button
            onClick={() => {
              handleSectionChange('home');
              // Scroll to search after navigation
              setTimeout(() => {
                const searchElement = document.querySelector('input[type="text"]');
                if (searchElement) {
                  searchElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  (searchElement as HTMLInputElement).focus();
                }
              }, 100);
            }}
            className={`flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-all ${
              isDarkMode
                ? 'text-gray-400 hover:text-gray-200'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Search className="w-5 h-5" />
            <span className="text-xs font-medium">
              {currentLanguage === 'en' && 'Search'}
              {currentLanguage === 'es' && 'Buscar'}
              {currentLanguage === 'so' && 'Raadi'}
              {currentLanguage === 'quc' && 'Tzukuxïk'}
            </span>
          </button>

          {/* Current Section Indicator */}
          {currentSection !== 'home' && (
            <div className={`flex flex-col items-center gap-1 px-4 py-2 rounded-xl bg-gradient-to-br from-blue-400 to-blue-500 text-white`}>
              <Sparkles className="w-5 h-5" />
              <span className="text-xs font-medium truncate max-w-20 text-center">
                {sections.find(s => s.id === currentSection)?.label[currentLanguage as keyof typeof sections[0]['label']]}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrenatalCareApp;
